/**
 * Ready stock classification and separation utilities for orders and product lines.
 */

import { stockPlacementSummary } from '../components/OrderInvoice/OrderInvoice';

/**
 * Check if an individual product line has ready stock available or fulfilled.
 *
 * True if:
 * 1. It was already fulfilled from ready stock (`fulfilledFromStock === true`)
 * 2. OR ready stock is available on-hand (`Number(availableInStock) > 0`)
 */
export const isLineReadyStock = (product) => {
  if (!product) return false;
  return Boolean(product.fulfilledFromStock) || (Number(product.availableInStock) > 0);
};

/**
 * Categorize an order by its ready stock status:
 *
 * - 'FULL': Every line in the order is either fulfilled or available in ready stock.
 *   (Can be picked directly from warehouse shelf, no factory production needed).
 * - 'PARTIAL': At least one line has ready stock, but other lines need production.
 *   (Requires shelf pickup for ready lines + factory production for the rest).
 * - 'NONE': No lines have ready stock. 100% factory production required.
 */
export const getOrderStockCategory = (order) => {
  const products = order?.products || [];
  if (!products.length) return 'NONE';

  let readyCount = 0;
  for (const p of products) {
    if (isLineReadyStock(p)) {
      readyCount++;
    }
  }

  if (readyCount === products.length) return 'FULL';
  if (readyCount > 0) return 'PARTIAL';
  return 'NONE';
};

/**
 * Whether the order has any ready stock involvement (FULL or PARTIAL).
 */
export const hasReadyStock = (order) => {
  const cat = getOrderStockCategory(order);
  return cat === 'FULL' || cat === 'PARTIAL';
};

/**
 * Summarize unique stock placement locations (shelves / bins) for all ready stock items in an order.
 * E.g., "Shelf A · B-12, Rack 2"
 */
export const orderPlacementSummary = (order) => {
  const placements = [];
  for (const p of (order?.products || [])) {
    if (isLineReadyStock(p)) {
      const loc = stockPlacementSummary(p);
      if (loc && !placements.includes(loc)) {
        placements.push(loc);
      }
    }
  }
  return placements.join(', ');
};

/**
 * Split an array of orders into ready stock orders (FULL and PARTIAL) and production orders (NONE).
 * Preserves relative order within each list.
 */
export const splitOrdersByStock = (orders = []) => {
  const readyStockOrders = [];
  const productionOrders = [];

  for (const order of orders) {
    if (hasReadyStock(order)) {
      readyStockOrders.push(order);
    } else {
      productionOrders.push(order);
    }
  }

  return { readyStockOrders, productionOrders };
};
